import { Schema } from 'prosemirror-model';
import { fullSchema } from '@chatwoot/prosemirror-schema';

const VARIABLE_REGEX = /([%#])\{([^}]+)\}/g;

export function getVariableCategory(name, prefix) {
  if (prefix === '#') return 'action';
  if (name.startsWith('__')) return 'system';
  return 'normal';
}

const variableNodeSpec = {
  attrs: {
    name: { default: '' },
    prefix: { default: '%' },
    label: { default: '' },
    category: { default: 'normal' },
  },
  group: 'inline',
  inline: true,
  selectable: true,
  draggable: false,
  atom: true,
  toDOM: node => {
    const classes = [
      'prosemirror-variable-chip',
      `prosemirror-variable-chip--${node.attrs.category}`,
    ].join(' ');
    return [
      'span',
      {
        class: classes,
        'data-variable-name': node.attrs.name,
        'data-variable-prefix': node.attrs.prefix,
        contenteditable: 'false',
      },
      node.attrs.label || `${node.attrs.prefix}{${node.attrs.name}}`,
    ];
  },
  parseDOM: [
    {
      tag: 'span[data-variable-name]',
      getAttrs: dom => {
        const name = dom.getAttribute('data-variable-name') || '';
        const prefix = dom.getAttribute('data-variable-prefix') || '%';
        return {
          name,
          prefix,
          label: dom.textContent,
          category: getVariableCategory(name, prefix),
        };
      },
    },
  ],
};

export const richTextSchemaWithVariables = new Schema({
  nodes: fullSchema.spec.nodes.addToEnd('variable', variableNodeSpec),
  marks: fullSchema.spec.marks,
});

function transformTextNode(node, schema, resolveLabel) {
  const text = node.textContent;
  const regex = new RegExp(VARIABLE_REGEX.source, 'g');

  if (!regex.test(text)) return node;

  regex.lastIndex = 0;

  const nodes = [];
  let lastIndex = 0;
  let match = regex.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push(schema.text(text.slice(lastIndex, match.index), node.marks));
    }

    const prefix = match[1];
    const name = match[2].replace(/\\\./g, '.');
    const { label, category } = resolveLabel(name, prefix);

    nodes.push(
      schema.nodes.variable.create({
        name,
        prefix,
        label: label || name,
        category,
      })
    );

    lastIndex = regex.lastIndex;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    nodes.push(schema.text(text.slice(lastIndex), node.marks));
  }

  return nodes;
}

function transformNode(node, schema, resolveLabel) {
  if (node.isText) {
    return transformTextNode(node, schema, resolveLabel);
  }

  if (node.content.size === 0) return node;

  const newChildren = [];
  let changed = false;

  node.forEach(child => {
    const result = transformNode(child, schema, resolveLabel);
    if (result !== child) changed = true;

    if (Array.isArray(result)) {
      newChildren.push(...result);
    } else {
      newChildren.push(result);
    }
  });

  if (!changed) return node;
  return node.type.create(node.attrs, newChildren, node.marks);
}

/**
 * After markdown parsing, walk the doc and replace %{...} / #{...} text
 * with variable atom nodes.
 */
export function textToVariableNodes(doc, schema, resolveLabel) {
  const fragments = [];
  let changed = false;

  doc.forEach(topNode => {
    const newChild = transformNode(topNode, schema, resolveLabel);
    if (newChild !== topNode) changed = true;
    fragments.push(newChild);
  });

  if (!changed) return doc;
  return schema.nodes.doc.create(null, fragments);
}

function revertNode(node, schema) {
  if (node.type.name === 'variable') {
    const { name, prefix } = node.attrs;
    return schema.text(`${prefix}{${name}}`);
  }

  if (node.content.size === 0) return node;

  const newChildren = [];
  let changed = false;

  node.forEach(child => {
    const result = revertNode(child, schema);
    if (result !== child) changed = true;
    newChildren.push(result);
  });

  if (!changed) return node;
  return node.type.create(node.attrs, newChildren, node.marks);
}

/**
 * Before serializing, walk the doc and replace variable nodes with
 * plain text %{...} / #{...} so the markdown serializer can handle it.
 */
export function variableNodesToText(doc, schema) {
  const fragments = [];
  let changed = false;

  doc.forEach(topNode => {
    const newChild = revertNode(topNode, schema);
    if (newChild !== topNode) changed = true;
    fragments.push(newChild);
  });

  if (!changed) return doc;
  return schema.nodes.doc.create(null, fragments);
}
